// myapp/src/api/appApi.ts
import axios, { InternalAxiosRequestConfig, AxiosResponse } from "axios";
import { handleSubscriptionError } from "./subscriptionInterceptor";

const appApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "https://localhost:7166/api/v1",
  withCredentials: true // Important for HttpOnly secure session cookies
});

// Request Interceptor: Inject App Bearer token from localStorage
appApi.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (!config.headers) return config;

    const token = localStorage.getItem('app_jwt_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response Interceptor: Handle automatic token renewals or clean 401/403 session expirations
appApi.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response && error.response.status === 401 && !originalRequest.url?.includes('/Auth/login') && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Attempt to call the refresh endpoint to renew short-lived JWT securely using HttpOnly cookie rotation
        const refreshResponse = await axios.post(
          `${appApi.defaults.baseURL}/Auth/refresh`, 
          {}, 
          { withCredentials: true }
        );
        
        const newJwt = refreshResponse.data.token;
        localStorage.setItem('app_jwt_token', newJwt);
        
        // Retry original request with the new token
        originalRequest.headers.Authorization = `Bearer ${newJwt}`;
        return appApi(originalRequest);
      } catch (refreshError) {
        // Refresh token rotation failed or expired. Force immediate logout.
        console.error("App session refresh failed, clearing security credentials:", refreshError);
        localStorage.removeItem('app_jwt_token');

        // Redirect strictly to app login screen
        window.location.href = '/login?expired=true';
      }
    }

    return handleSubscriptionError(error, appApi, 'app_jwt_token');
  }
);

export default appApi;
